import { useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface TooltipPosition {
  left: number;
  top: number;
  below: boolean;
}

/** A viewport-level tooltip that cannot be clipped by table/log overflow containers. */
export function AdminStatsInfoTip({ text }: { text: string }) {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tooltipId = useId();
  const [position, setPosition] = useState<TooltipPosition | null>(null);

  const show = () => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const halfWidth = Math.min(140, Math.max(0, (viewportWidth - 24) / 2));
    const center = rect.left + rect.width / 2;
    const left = viewportWidth < 304
      ? viewportWidth / 2
      : Math.max(halfWidth + 12, Math.min(viewportWidth - halfWidth - 12, center));
    const below = rect.top < 130;
    setPosition({
      left,
      top: below ? rect.bottom + 8 : rect.top - 8,
      below,
    });
  };

  const hide = () => setPosition(null);

  return (
    <>
      <span
        ref={anchorRef}
        className="astats-info-tip"
        tabIndex={0}
        aria-label={text}
        aria-describedby={position ? tooltipId : undefined}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        i
      </span>
      {position && typeof document !== 'undefined' && createPortal(
        <span
          id={tooltipId}
          className={`astats-info-tip__bubble${position.below ? ' astats-info-tip__bubble--below' : ''}`}
          role="tooltip"
          style={{ left: position.left, top: position.top }}
        >
          {text}
        </span>,
        document.body,
      )}
    </>
  );
}
